import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle2, XCircle } from "lucide-react";
import { OgmaLogo } from "@/components/OgmaLogo";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { ErrorAlert } from "@/components/ui/ErrorAlert";
import { authApi } from "@/features/auth/api";
import { extractMessage } from "@/lib/errors";

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError(t("common.passwordMismatch"));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await authApi.confirmPasswordReset(token, newPassword);
      setDone(true);
    } catch (err) {
      setError(extractMessage(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex h-screen w-screen items-center justify-center bg-muted/40">
      <div className="flex w-80 flex-col items-center gap-6 rounded-xl border border-border bg-card px-10 py-12 text-center shadow-sm">
        <OgmaLogo size={40} />
        {!token ? (
          <>
            <XCircle className="size-8 text-destructive" />
            <div className="flex flex-col gap-1">
              <h1 className="text-lg font-semibold text-foreground">
                {t("passwordReset.invalidTokenTitle")}
              </h1>
              <p className="text-sm text-muted-foreground">
                {t("passwordReset.invalidTokenDescription")}
              </p>
            </div>
            <Button className="w-full" render={<Link to="/forgot-password" />}>
              {t("passwordReset.requestNewLink")}
            </Button>
          </>
        ) : done ? (
          <>
            <CheckCircle2 className="size-8 text-primary" />
            <div className="flex flex-col gap-1">
              <h1 className="text-lg font-semibold text-foreground">
                {t("passwordReset.resetSuccessTitle")}
              </h1>
              <p className="text-sm text-muted-foreground">
                {t("passwordReset.resetSuccessDescription")}
              </p>
            </div>
            <Button className="w-full" render={<Link to="/login" />}>
              {t("passwordReset.backToLogin")}
            </Button>
          </>
        ) : (
          <>
            <div className="flex flex-col gap-1">
              <h1 className="text-lg font-semibold text-foreground">
                {t("passwordReset.resetTitle")}
              </h1>
              <p className="text-sm text-muted-foreground">{t("passwordReset.resetDescription")}</p>
            </div>
            <form onSubmit={handleSubmit} className="flex w-full flex-col gap-3 text-left">
              <Input
                type="password"
                placeholder={t("passwordReset.newPasswordPlaceholder")}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                minLength={8}
                required
                autoFocus
              />
              <Input
                type="password"
                placeholder={t("common.confirmPasswordPlaceholder")}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                minLength={8}
                required
              />
              {error && <ErrorAlert message={error} />}
              <Button type="submit" disabled={loading} className="w-full">
                {loading ? t("passwordReset.resetSubmitting") : t("passwordReset.resetSubmit")}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
